/**
 * BulkEditPanel — spreadsheet-style bulk editor for initiative/axis/ring/position.
 *
 * Shows all currently-filtered nodes with per-row initiative, axis, and ring dropdowns.
 * "Quick-set" row fills the selected rows (all selected by default; WQ-343).
 * Auto-Position (opt-in per row, unchecked by default) computes map placement.
 * "Apply All" commits changes in a single undo-able batch via replaceAllNodes.
 */

import { useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Zap, Check } from 'lucide-react';
import type {
  AdjacencyNode,
  AdjacencyAxis,
  RelatednessRing,
  MapPosition,
  StrategicInitiative,
} from '../../types/adjacency';
import { AXIS_ANGLES, RING_SUCCESS_RATES } from '../../types/adjacency';
import { RING_BANDS } from '../../utils/positioning';
import { AXIS_COLORS } from '../../utils/view-colors';
import { INITIATIVE_COLORS } from '../../utils/map-colors';
import { GlassSelect } from '../shared/GlassSelect';
import type { GlassSelectOption } from '../shared/GlassSelect';

// ── Constants ─────────────────────────────────────────────────────────

const ALL_INITIATIVES: StrategicInitiative[] = [
  'US Mining', 'Proximity Detection', 'Analytics', 'Maritime',
  'International Mining', 'New Verticals', 'Capabilities', 'Other',
];

const ALL_AXES: AdjacencyAxis[] = [
  'Core', 'New Services', 'New Markets & Customers', 'Forward Integration',
  'New Technologies & Capabilities', 'New Channels', 'New Geographies',
  'Backward Integration', 'New Products',
];

const ALL_RINGS: RelatednessRing[] = [0, 1, 2, 3, 4];

const INITIATIVE_OPTIONS: GlassSelectOption[] = [
  { value: '', label: '— no change —' },
  ...ALL_INITIATIVES.map(i => ({ value: i, label: i })),
];

const QUICK_INITIATIVE_OPTIONS: GlassSelectOption[] = [
  { value: '', label: '— Initiative —' },
  ...ALL_INITIATIVES.map(i => ({ value: i, label: i })),
];

const AXIS_OPTIONS: GlassSelectOption[] = [
  { value: '', label: '— no change —' },
  ...ALL_AXES.map(a => ({ value: a, label: a })),
];

const QUICK_AXIS_OPTIONS: GlassSelectOption[] = [
  { value: '', label: '— Axis —' },
  ...ALL_AXES.map(a => ({ value: a, label: a })),
];

const RING_OPTIONS: GlassSelectOption[] = [
  { value: '', label: '— no change —' },
  ...ALL_RINGS.map(r => ({ value: String(r), label: `${r} — ${RING_SUCCESS_RATES[r].label}` })),
];

const QUICK_RING_OPTIONS: GlassSelectOption[] = [
  { value: '', label: '— Ring —' },
  ...ALL_RINGS.map(r => ({ value: String(r), label: `${r} — ${RING_SUCCESS_RATES[r].label}` })),
];

/** Remap constants matching positioning.ts */
const CORE_OFFSET = 0.12;
const REMAP_SCALE = 0.95 - CORE_OFFSET; // 0.83

// ── Auto-Position Math ───────────────────────────────────────────────

function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** Convert CW-from-top degrees to SVG/math radians (same coord system as mapPosition). */
function cwTopToMathRad(cwDeg: number): number {
  return degToRad(cwDeg - 90);
}

/**
 * Compute a normalized mapPosition for a given axis/ring.
 * Distributes multiple nodes at the same axis/ring by spreading the angle.
 */
function computeAutoPosition(
  axis: AdjacencyAxis,
  ring: RelatednessRing,
  index: number,
  total: number,
): MapPosition {
  if (axis === 'Core' || ring === 0) {
    // Core nodes: small circle around center
    if (total === 1) return { x: 0, y: 0 };
    const step = (2 * Math.PI) / total;
    const angle = -Math.PI / 2 + index * step;
    return { x: 0.04 * Math.cos(angle), y: 0.04 * Math.sin(angle) };
  }

  const axisAngle = AXIS_ANGLES[axis];
  const { inner, outer } = RING_BANDS[ring];
  const targetR = (inner + outer) / 2;

  // Invert the remap: mapPosition rawR → rendered targetR
  const rawR = (targetR - CORE_OFFSET) / REMAP_SCALE;

  // Spread multiple nodes +-2° per node around the axis center
  const spreadDeg = total > 1 ? 3 : 0;
  const offsetDeg = total > 1
    ? -spreadDeg * (total - 1) / 2 + index * spreadDeg
    : 0;

  const rad = cwTopToMathRad(axisAngle + offsetDeg);
  return {
    x: rawR * Math.cos(rad),
    y: rawR * Math.sin(rad),
  };
}

// ── Types ─────────────────────────────────────────────────────────────

interface RowEdit {
  newInitiative: StrategicInitiative | null; // null = unchanged
  newAxis: AdjacencyAxis | null;   // null = unchanged
  newRing: RelatednessRing | null; // null = unchanged
  autoPosition: boolean;
}

interface BulkEditPanelProps {
  nodes: AdjacencyNode[];           // filtered nodes to edit
  allNodes: AdjacencyNode[];        // full node array for batch replace
  onApply: (updatedNodes: AdjacencyNode[]) => void;
  onClose: () => void;
}

// ── Component ─────────────────────────────────────────────────────────

export function BulkEditPanel({ nodes, allNodes, onApply, onClose }: BulkEditPanelProps) {
  // Per-row edits keyed by node id
  const [edits, setEdits] = useState<Map<string, RowEdit>>(() => {
    const map = new Map<string, RowEdit>();
    for (const n of nodes) {
      map.set(n.id, { newInitiative: null, newAxis: null, newRing: null, autoPosition: false });
    }
    return map;
  });

  // WQ-343: Quick Set applies to selected rows only — all selected by default
  const [selected, setSelected] = useState<Set<string>>(() => new Set(nodes.map(n => n.id)));
  const allSelected = selected.size === nodes.length && nodes.length > 0;

  const toggleRow = useCallback((nodeId: string, on: boolean) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (on) next.add(nodeId); else next.delete(nodeId);
      return next;
    });
  }, []);

  const toggleAll = useCallback((on: boolean) => {
    setSelected(on ? new Set(nodes.map(n => n.id)) : new Set());
  }, [nodes]);

  // Quick-set values
  const [quickInitiative, setQuickInitiative] = useState<StrategicInitiative | ''>('');
  const [quickAxis, setQuickAxis] = useState<AdjacencyAxis | ''>('');
  const [quickRing, setQuickRing] = useState<RelatednessRing | ''>('');

  const modifiedCount = useMemo(() => {
    let count = 0;
    for (const [, edit] of edits) {
      if (edit.newInitiative !== null || edit.newAxis !== null || edit.newRing !== null) count++;
    }
    return count;
  }, [edits]);

  const updateRow = useCallback((nodeId: string, patch: Partial<RowEdit>) => {
    setEdits(prev => {
      const next = new Map(prev);
      const current = next.get(nodeId);
      if (current) next.set(nodeId, { ...current, ...patch });
      return next;
    });
  }, []);

  const applyQuickSet = useCallback(() => {
    setEdits(prev => {
      const next = new Map(prev);
      for (const [id, edit] of next) {
        if (!selected.has(id)) continue;
        const patch: Partial<RowEdit> = {};
        if (quickInitiative !== '') patch.newInitiative = quickInitiative;
        if (quickAxis !== '') patch.newAxis = quickAxis;
        if (quickRing !== '') patch.newRing = quickRing;
        next.set(id, { ...edit, ...patch });
      }
      return next;
    });
  }, [quickInitiative, quickAxis, quickRing, selected]);

  const quickSetEmpty = quickInitiative === '' && quickAxis === '' && quickRing === '';
  const quickSetDisabled = quickSetEmpty || selected.size === 0;

  const handleApply = useCallback(() => {
    // Group modified nodes by their target (axis, ring) for position distribution
    const positionGroups = new Map<string, string[]>();

    for (const node of nodes) {
      const edit = edits.get(node.id);
      if (!edit || (edit.newInitiative === null && edit.newAxis === null && edit.newRing === null)) continue;
      if (!edit.autoPosition) continue;

      const targetAxis = edit.newAxis ?? node.adjacencyAxis;
      const targetRing = edit.newRing ?? node.relatednessRing;
      const key = `${targetAxis}|${targetRing}`;
      const group = positionGroups.get(key) ?? [];
      group.push(node.id);
      positionGroups.set(key, group);
    }

    // Build modified node lookup
    const modifiedNodes = new Map<string, Partial<AdjacencyNode>>();

    for (const node of nodes) {
      const edit = edits.get(node.id);
      if (!edit || (edit.newInitiative === null && edit.newAxis === null && edit.newRing === null)) continue;

      const changes: Partial<AdjacencyNode> = {};
      const targetAxis = edit.newAxis ?? node.adjacencyAxis;
      const targetRing = edit.newRing ?? node.relatednessRing;

      if (edit.newInitiative !== null) changes.strategicInitiative = edit.newInitiative;
      if (edit.newAxis !== null) changes.adjacencyAxis = edit.newAxis;
      if (edit.newRing !== null) changes.relatednessRing = edit.newRing;

      if (edit.autoPosition) {
        const key = `${targetAxis}|${targetRing}`;
        const group = positionGroups.get(key)!;
        const idx = group.indexOf(node.id);
        changes.mapPosition = computeAutoPosition(targetAxis, targetRing, idx, group.length);
      }

      changes.modifiedAt = new Date().toISOString();
      modifiedNodes.set(node.id, changes);
    }

    if (modifiedNodes.size === 0) {
      onClose();
      return;
    }

    // Build full replacement array
    const updated = allNodes.map(n => {
      const changes = modifiedNodes.get(n.id);
      if (!changes) return n;
      return { ...n, ...changes };
    });

    onApply(updated);
    onClose();
  }, [nodes, edits, allNodes, onApply, onClose]);

  // WQ-343: portalled above the admin sidebar like the module's other modals
  // (rendered in place, the sidebar covered the panel's left columns at ≤ 1280px)
  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center"
      style={{ background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Bulk Edit"
        className="relative flex flex-col max-h-[85vh] w-[90vw] max-w-[1100px] rounded-xl overflow-hidden"
        style={{
          background: 'var(--modal-bg)',
          border: '1px solid var(--modal-border)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3 border-b"
          style={{ borderColor: 'var(--glass-border)' }}
        >
          <div>
            <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              Bulk Edit — Initiative, Axis & Ring
            </h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              {nodes.length} nodes in current filter &middot; {modifiedCount} modified
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md transition-colors"
            style={{ color: 'var(--text-dim)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--glass-bg-elevated)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Quick-set row */}
        <div
          className="flex items-center gap-3 px-5 py-2.5 border-b"
          style={{
            borderColor: 'var(--glass-border)',
            background: 'rgba(59, 130, 246, 0.06)',
          }}
        >
          <Zap size={13} style={{ color: '#60a5fa', flexShrink: 0 }} />
          <span className="text-xs font-medium" style={{ color: '#93c5fd', flexShrink: 0 }}>
            Set selected to:
          </span>
          <div style={{ width: 160 }}>
            <GlassSelect
              value={quickInitiative}
              options={QUICK_INITIATIVE_OPTIONS}
              onChange={v => setQuickInitiative(v as StrategicInitiative | '')}
              placeholder="— Initiative —"
              compact
            />
          </div>
          <div style={{ width: 180 }}>
            <GlassSelect
              value={quickAxis}
              options={QUICK_AXIS_OPTIONS}
              onChange={v => setQuickAxis(v as AdjacencyAxis | '')}
              placeholder="— Axis —"
              compact
            />
          </div>
          <div style={{ width: 160 }}>
            <GlassSelect
              value={String(quickRing)}
              options={QUICK_RING_OPTIONS}
              onChange={v => setQuickRing(v === '' ? '' : Number(v) as RelatednessRing)}
              placeholder="— Ring —"
              compact
            />
          </div>
          <button
            onClick={applyQuickSet}
            disabled={quickSetDisabled}
            className="text-xs px-2.5 py-1 rounded font-medium transition-colors"
            style={{
              background: quickSetDisabled ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.25)',
              color: quickSetDisabled ? '#475569' : '#93c5fd',
              border: '1px solid rgba(59,130,246,0.2)',
              cursor: quickSetDisabled ? 'not-allowed' : 'pointer',
            }}
          >
            Apply to {selected.size} selected
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto px-1">
          <table className="w-full text-xs border-collapse" style={{ color: 'var(--text-secondary)' }}>
            <thead>
              <tr style={{ background: 'var(--glass-bg-elevated)' }}>
                <Th align="center">
                  <input
                    type="checkbox"
                    aria-label="Select all rows"
                    checked={allSelected}
                    ref={el => { if (el) el.indeterminate = selected.size > 0 && !allSelected; }}
                    onChange={e => toggleAll(e.target.checked)}
                    className="accent-blue-500 cursor-pointer"
                  />
                </Th>
                <Th>Name</Th>
                <Th>Initiative</Th>
                <Th>New Initiative</Th>
                <Th>Current Axis</Th>
                <Th>New Axis</Th>
                <Th>Current Ring</Th>
                <Th>New Ring</Th>
                <Th align="center">Auto-Pos</Th>
              </tr>
            </thead>
            <tbody>
              {nodes.map((node, i) => {
                const edit = edits.get(node.id)!;
                const isModified = edit.newInitiative !== null || edit.newAxis !== null || edit.newRing !== null;
                return (
                  <tr
                    key={node.id}
                    data-node-id={node.id}
                    className="border-b transition-colors"
                    style={{
                      borderColor: 'var(--glass-border)',
                      background: isModified
                        ? 'rgba(59, 130, 246, 0.08)'
                        : i % 2 === 0 ? 'transparent' : 'var(--table-row-alt-bg)',
                    }}
                  >
                    {/* Selection (Quick Set target) */}
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        aria-label={`Select ${node.name}`}
                        checked={selected.has(node.id)}
                        onChange={e => toggleRow(node.id, e.target.checked)}
                        className="accent-blue-500 cursor-pointer"
                      />
                    </td>

                    {/* Name */}
                    <td className="px-3 py-2 font-medium max-w-48 truncate" style={{ color: 'var(--text-primary)' }}>
                      {node.name}
                    </td>

                    {/* Initiative */}
                    <td className="px-3 py-2">
                      <Badge label={node.strategicInitiative} color={INITIATIVE_COLORS[node.strategicInitiative]} />
                    </td>

                    {/* New Initiative */}
                    <td className="px-3 py-2">
                      <GlassSelect
                        value={edit.newInitiative ?? ''}
                        options={INITIATIVE_OPTIONS}
                        onChange={v => updateRow(node.id, {
                          newInitiative: v === '' ? null : v as StrategicInitiative,
                        })}
                        placeholder="— no change —"
                        compact
                        highlight={edit.newInitiative !== null}
                      />
                    </td>

                    {/* Current Axis */}
                    <td className="px-3 py-2">
                      <Badge
                        label={node.adjacencyAxis === 'New Technologies & Capabilities' ? 'New Tech' : node.adjacencyAxis}
                        color={AXIS_COLORS[node.adjacencyAxis]}
                      />
                    </td>

                    {/* New Axis */}
                    <td className="px-3 py-2">
                      <GlassSelect
                        value={edit.newAxis ?? ''}
                        options={AXIS_OPTIONS}
                        onChange={v => updateRow(node.id, {
                          newAxis: v === '' ? null : v as AdjacencyAxis,
                        })}
                        placeholder="— no change —"
                        compact
                        highlight={edit.newAxis !== null}
                      />
                    </td>

                    {/* Current Ring */}
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      {node.relatednessRing} — {RING_SUCCESS_RATES[node.relatednessRing].label}
                    </td>

                    {/* New Ring */}
                    <td className="px-3 py-2">
                      <GlassSelect
                        value={edit.newRing !== null ? String(edit.newRing) : ''}
                        options={RING_OPTIONS}
                        onChange={v => updateRow(node.id, {
                          newRing: v === '' ? null : Number(v) as RelatednessRing,
                        })}
                        placeholder="— no change —"
                        compact
                        highlight={edit.newRing !== null}
                      />
                    </td>

                    {/* Auto-Position */}
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={edit.autoPosition}
                        onChange={e => updateRow(node.id, { autoPosition: e.target.checked })}
                        className="accent-blue-500 cursor-pointer"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between px-5 py-3 border-t"
          style={{ borderColor: 'var(--glass-border)' }}
        >
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {modifiedCount === 0
              ? 'No changes — select axis or ring for at least one row'
              : `${modifiedCount} node${modifiedCount > 1 ? 's' : ''} will be updated`}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="text-xs px-3 py-1.5 rounded font-medium transition-colors"
              style={{
                background: 'var(--glass-bg)',
                color: 'var(--text-secondary)',
                border: '1px solid var(--glass-border)',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--glass-bg)'; }}
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={modifiedCount === 0}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded font-medium transition-colors"
              style={{
                background: modifiedCount === 0 ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.3)',
                color: modifiedCount === 0 ? '#475569' : '#93c5fd',
                border: '1px solid rgba(59,130,246,0.3)',
                cursor: modifiedCount === 0 ? 'not-allowed' : 'pointer',
              }}
              onMouseEnter={e => {
                if (modifiedCount > 0) e.currentTarget.style.background = 'rgba(59,130,246,0.45)';
              }}
              onMouseLeave={e => {
                if (modifiedCount > 0) e.currentTarget.style.background = 'rgba(59,130,246,0.3)';
              }}
            >
              <Check size={13} /> Apply All
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Sub-Components ────────────────────────────────────────────────────

function Th({ children, align }: { children: React.ReactNode; align?: 'center' | 'left' }) {
  return (
    <th
      className={`px-3 py-2 font-medium whitespace-nowrap border-b text-${align ?? 'left'}`}
      style={{ borderColor: 'var(--glass-border)', color: 'var(--text-muted)' }}
    >
      {children}
    </th>
  );
}

function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span
      className="inline-block text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap"
      style={{ background: color + '22', color }}
    >
      {label}
    </span>
  );
}
